import React from 'react';
import '../styles/TypingTestExtras.css';

const clickFeatures = [
  {
    icon: '⚡',
    title: 'Boost Your Reflexes',
    text: 'Click speed tests are a fun way to sharpen your reaction time and hand-eye coordination. Great for gamers and anyone wanting to improve their reflexes!'
  },
  {
    icon: '🖱️',
    title: 'Test Your Mouse & Trackpad',
    text: 'See how your mouse or trackpad performs under pressure. Find out if your gear is holding you back or helping you click faster.'
  },
  {
    icon: '🏆',
    title: 'Compete & Challenge Friends',
    text: 'Share your CPS score and challenge friends to beat your record. Who will be the fastest clicker in your group?'
  },
  {
    icon: '🧠',
    title: 'Fun Brain Break',
    text: "Take a quick break from work or study with a click speed test. It's a simple, satisfying way to reset your mind!"
  },
];

const richClickContent = {
  1: {
    h2: 'The 1-Second Reflex Test',
    intro: 'Welcome to a quick test of your reflexes. Our 1-second challenge measures your immediate clicking speed. It\'s very fast, but it shows how quickly you can react. Ready to see how fast your clicking finger is?',
    sections: [
      { h3: 'Why Test for Just One Second?', p: 'This test is like taking a photo of your fastest possible click. It measures your immediate reaction time and gives you a raw CPS (clicks per second) score. It is a great way to warm up before playing a game.' },
      { h3: 'How Does It Work?', p: 'It\'s very easy. Place your finger on your mouse button. When you click the box for the first time, the 1-second timer begins. Your goal is to click as many times as you can before time runs out. Your score is your instant CPS.' },
      { h3: 'A Tip for a Better Score:', p: 'Try not to overthink it; this test is about instinct. Make sure your mouse is on a flat surface and your hand is comfortable. A relaxed grip often helps you click faster and more easily.' },
      { h3: 'A Quick Competition!', p: 'Who is the fastest clicker among your friends? The 1-second test is a perfect way to find out. Share your score and challenge them to see who gets a higher CPS.' },
      { h3: 'Ready to Test Your Speed?', p: 'Your 1-second challenge is waiting. Scroll up, get ready, and start the test when you\'re prepared. Let\'s see how you do. Good luck!' },
    ]
  },
  5: {
    h2: 'The 5-Second Click Speed Test',
    intro: 'Ready for a slightly longer challenge? The 5-second test lets you find a rhythm. It\'s a good way to measure your clicking speed over a short burst and see how many clicks you can land.',
    sections: [
      { h3: 'What is the 5-Second Advantage?', p: 'Five seconds is a great time for measuring your \'burst speed,\' which is useful in many games. It\'s long enough to show your rhythm, giving a more stable CPS score than the 1-second test.' },
      { h3: 'How to Take the Test:', p: 'Get your hand in a comfortable position. The 5-second timer starts with your first click. Try to keep your clicking speed high for the whole time to get the best possible score.' },
      { h3: 'Tips for Clicking Faster:', p: 'Find a comfortable way to hold your mouse that lets you click quickly. Try to make your clicks light and fast instead of pressing the button down hard. This will help you get more clicks per second.' },
      { h3: 'Challenge Your Friends!', p: 'This test is great for a friendly competition. See who can get the highest CPS in this quick challenge. Competing with friends is a good way to practice and improve your clicking skills.' },
      { h3: 'Ready to Begin?', p: 'It\'s time to test your clicking speed. Scroll to the top of the page to start the 5-second challenge. Focus on your rhythm and good luck!' },
    ]
  },
  15: {
    h2: 'The 15-Second Click Challenge',
    intro: 'In the 15-second test, you need both speed and a bit of endurance. It\'s a good challenge to see if you can keep a fast pace for a longer period. Let\'s find out how well you can do!',
    sections: [
      { h3: 'Why a 15-Second Test?', p: 'This duration is a good way to measure your practical clicking skill. It\'s longer than a quick burst and shows if you can stay focused. This can be similar to situations in games that require fast, repeated clicking.' },
      { h3: 'How This Challenge Works:', p: 'Get comfortable and prepare your clicking finger. The 15-second timer begins with your first click. Your goal is to maintain a steady and fast clicking speed until the time is up.' },
      { h3: 'Tips for a High Score:', p: 'Try to find a good pace. You want to be fast, but keeping your arm relaxed is important. A steady hand is the most important thing for a good score, more than any special technique.' },
      { h3: 'Practice with Friends!', p: 'Practicing is more fun with others. Challenge your friends to the 15-second test and see who gets the best score. Tracking your CPS score over time is a great way to see your improvement.' },
      { h3: 'Ready to Set a New Record?', p: 'The test is at the top of the page. Get in position, focus, and try to set a new personal best. Good luck!' },
    ]
  },
  30: {
    h2: 'The 30-Second Endurance Test',
    intro: 'Welcome to the 30-second test. This challenge is a good measure of your clicking consistency and endurance. It shows if you can keep clicking quickly for a longer time.',
    sections: [
      { h3: 'Why Test for 30 Seconds?', p: 'Thirty seconds is long enough to show your true, consistent clicking speed (CPS). It\'s a reliable way to measure your endurance and focus. A good score in this test shows real clicking skill.' },
      { h3: 'How to Do the 30-Second Test:', p: 'Get comfortable, as this test is a bit longer. The timer starts when you click. The key is to find a fast rhythm that you can maintain without your hand getting tired. Consistency is very important here.' },
      { h3: 'Tips for Better Endurance:', p: 'Try not to start too fast and then slow down. It\'s better to keep a steady, high speed for the whole 30 seconds. Remember to keep your hand and arm relaxed to avoid getting tired.' },
      { h3: 'A Great Test for Competition', p: 'The 30-second test is a great way to compare your score with friends. You can see who has better clicking endurance and motivate each other to improve your skills.' },
      { h3: 'Ready to Test Your Endurance?', p: 'It\'s time to see what you can do. Scroll up to begin the 30-second challenge. Focus, find your rhythm, and do your best!' },
    ]
  },
  60: {
    h2: 'The 1-Minute Click Test',
    intro: 'This is the standard 1-minute click test. It is a full test of your clicking finger and a true challenge of your speed, focus, and endurance. Are you ready to get your best score?',
    sections: [
      { h3: 'What\'s Special About a Full Minute?', p: 'A full minute really tests your stamina. You can\'t just rely on a short burst of speed. This test gives a very accurate CPS score because it measures your performance over a longer time.' },
      { h3: 'How to Complete the 1-Minute Test:', p: 'Find a comfortable position. For the next 60 seconds, your goal is to click as consistently as possible. Try to manage any fatigue and maintain a steady pace through the entire minute.' },
      { h3: 'Secrets to a Good 1-Minute Score:', p: 'Pacing is the most important part of this test. Don\'t start at your maximum speed. Instead, choose a fast speed that you can keep for the whole minute. If your hand gets tired, it\'s okay to pause for a second to relax it.' },
      { h3: 'A Score to Be Proud Of', p: 'Finishing the 1-minute test is a good achievement. You should be proud of your score. Comparing 1-minute scores is a great way to see who is the most consistent clicker among your friends.' },
      { h3: 'Your 1-Minute Challenge Starts Now!', p: 'This is the longest test. Scroll to the top, take a breath, and start your 1-minute challenge when you\'re ready. Good luck, and we hope you set a new personal record!' },
    ]
  }
};

const getIntroText = (duration) => {
  switch (duration) {
    case 1:
      return "Test your clicking speed in just 1 second! Perfect for quick reflexes and rapid clicking practice.";
    case 5:
      return "Challenge yourself with a 5-second click speed test. Great for improving your clicking accuracy and speed.";
    case 15:
      return "Take on the 15-second click test to measure your sustained clicking performance. Ideal for gaming and productivity.";
    case 30:
      return "Master the 30-second click speed challenge. Perfect for testing your endurance and consistent clicking ability.";
    case 60:
      return "Push your limits with the 60-second click speed test. The ultimate challenge for measuring your clicking stamina.";
    default:
      return "Test your clicking speed and improve your CPS (Clicks Per Second) with our various duration options.";
  }
};

export default function ClickTestExtras({ duration = 5 }) {
  const content = richClickContent[duration] || richClickContent[5];
  return (
    <>
      {/* Features Section */}
      <div className="features-wrapper">
        <section className="features-section">
          <h2>Why Try Click Speed Tests?</h2>
          <div className="features-container">
            {clickFeatures.map((f, i) => (
              <div className="feature-box" key={i}>
                <span style={{fontSize: '2em', marginRight: 16}}>{f.icon}</span>
                <p><strong>{f.title}:</strong> {f.text}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* What is a Click Speed Test Section */}
      <div className="wrapper-intro">
        <h2>{content.h2}</h2>
        <p className="intro">{getIntroText(duration)}</p>
        {content.sections.map((section, idx) => (
          <div key={idx}>
            <h3>{section.h3}</h3>
            <p>{section.p}</p>
          </div>
        ))}
      </div>
    </>
  );
} 